// @flow
import {
  STATUS_PENDING,
  STATUS_IN_PROGRESS,
  STATUS_STAGED,
  STATUS_COMPLETE,
  STATUS_ERROR,
} from 'box-ui-elements/es/constants';
import { isMultiputSupported } from 'box-ui-elements/es/utils/uploads';
import {
  getTypedFileId,
  getTypedFolderId,
} from 'box-ui-elements/es/utils/file';
import API from 'box-ui-elements/es/api';
import get from 'lodash/get';
import union from 'lodash/union';
import map from 'lodash/map';
import noop from 'lodash/noop';
import set from 'lodash/set';
import forEach from 'lodash/forEach';
import cloneDeep from 'lodash/cloneDeep';
import type { BoxItem } from 'box-ui-elements/es/common/types/core';
import type {
  UploadItem,
  UploadItemAPIOptions,
} from 'box-ui-elements/es/common/types/upload';
import sha1 from 'js-sha1';
import resin from '../../common/utils/resin';

const CHUNKED_UPLOAD_MIN_SIZE_BYTES = 104857600; // 100MB
const UPLOAD_CONCURRENCY = 6;

const defaultOptions = {
  onUploadProgressUpdate: noop,
  chunked: true,
  isResumableUploadsEnabled: true,
  overwrite: false,
  conflictCallback: null,
  token: null,
};

const resinEvent = {
  action: 'programmatic',
  page: 'file-request-submission',
  feature: 'filerequest',
  component: 'submissionpage',
};

class Uploader {
  rootFolderId: string;

  itemQueues: Object;

  itemsInUploadQueue: Array<UploadItem>;

  options: Object;

  fileRequestUrlId: ?string;

  formResponseKey: ?string;

  fetchNewToken: Function;

  constructor(
    rootFolderId: string,
    itemQueues: Object = {},
    options: Object = {},
  ) {
    this.rootFolderId = rootFolderId;
    this.itemQueues = itemQueues;
    this.options = { ...defaultOptions, ...options };
    this.itemsInUploadQueue = [];
    this.formResponseKey = null;
    this.fetchNewToken = () => Promise.resolve({});
  }

  /**
   * destroys the object, for use when external APIs error out
   */
  destroy() {
    this.itemsInUploadQueue.forEach(uploadItem => {
      if (uploadItem.api) uploadItem.api.destroy();
    });
  }

  /**
   * Create and return new instance of API creator
   *
   * @param {UploadItemAPIOptions} [uploadAPIOptions]
   * @return {API}
   */
  createAPIFactory(uploadAPIOptions?: UploadItemAPIOptions) {
    const { rootFolderId } = this;
    const folderId = get(uploadAPIOptions, 'folderId') || rootFolderId;
    const fileId = get(uploadAPIOptions, 'fileId');
    const itemFolderId = getTypedFolderId(folderId);
    const itemFileId = fileId ? getTypedFileId(fileId) : null;

    return new API({
      ...this.getBaseAPIOptions(),
      id: itemFileId || itemFolderId,
      ...uploadAPIOptions,
    });
  }

  /**
   * Return base API options from props
   *
   * @private
   * @returns {Object}
   */
  getBaseAPIOptions(): Object {
    const { token, apiHost } = this.options;

    return {
      token,
      apiHost,
      uploadHost: apiHost,
      requestInterceptor: config => {
        config.url = config.url.replace('2.0', '2.1');
        return config;
      },
      responseInterceptor: response => {
        if (response.status && response.status === 201) {
          const { data } = response;
          data.entries = [];
          data.entries.push(data);
        }
        return response;
      },
    };
  }

  /**
   * Returns a new API instance for the given file.
   *
   * @private
   * @param {UploadItem} item - UploadItem to get a new API instance for
   * @param {UploadItemAPIOptions} [uploadAPIOptions]
   * @return {UploadAPI} - Instance of Upload API
   */
  getUploadAPI(item: UploadItem, uploadAPIOptions?: UploadItemAPIOptions) {
    // VG: commented to temporrily remove multiputs.
    const { chunked, isResumableUploadsEnabled } = this.options;
    const { size } = item;
    // VG: Temporary change end
    const factory = this.createAPIFactory(uploadAPIOptions);

    // VG: commented to temporrily remove multiputs.
    if (chunked && size > CHUNKED_UPLOAD_MIN_SIZE_BYTES) {
      if (isMultiputSupported()) {
        const chunkedUploadAPI = factory.getChunkedUploadAPI();
        if (isResumableUploadsEnabled) {
          chunkedUploadAPI.isResumableUploadsEnabled = true;
        }
        return chunkedUploadAPI;
      }
      /* eslint-disable no-console */
      console.warn(
        'Chunked uploading is enabled, but not supported by your browser. You may need to enable HTTPS.',
      );
      /* eslint-enable no-console */
    }
    // VG: Temporary change end
    return factory.getPlainUploadAPI();
  }

  async getSHA1ForItem(item: UploadItem) {
    const readFile = (blob: Blob) => {
      const reader = new window.FileReader();
      return new Promise((resolve, reject) => {
        reader.readAsArrayBuffer(blob);
        reader.onload = () => {
          resolve({
            buffer: reader.result,
            readCompleteTimestamp: Date.now(),
          });
        };
        reader.onerror = reject;
      });
    };
    const computeSHA1 = async file => {
      // copied from box-ui-elements webcrypto + plainUpload
      // used when sha is returned as empty string (EDGE scenario)
      const { buffer } = await readFile(file);
      const hashBuffer = sha1.arrayBuffer(buffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => `00${b.toString(16)}`.slice(-2)).join('');
    };
    const { options, file } = item;
    const factory = this.createAPIFactory(options);
    const plainUploadAPI = factory.getPlainUploadAPI();
    const sha =
      (await plainUploadAPI.computeSHA1(file)) || (await computeSHA1(file));
    return sha;
  }

  checkConcurrencyLimit() {
    const numItemsUploading = this.itemsInUploadQueue.filter(
      item => item.status === STATUS_IN_PROGRESS,
    ).length;

    if (numItemsUploading >= UPLOAD_CONCURRENCY) {
      return false;
    }
    return true;
  }

  /**
   * Helper to reset a file. Cancels any current upload and resets progress.
   *
   * @param {UploadItem} item - Upload item to reset
   * @return {void}
   */
  resetFile(item: UploadItem) {
    const { api, file, options } = item;
    if (api && typeof api.cancel === 'function') {
      api.cancel();
    }

    // Reset API, progress & status
    item.api = this.getUploadAPI(file, options);
    item.progress = 0;
    item.status = STATUS_PENDING;
    delete item.error;
    this.options.onUploadProgressUpdate(item, this);
  }

  /**
   * Helper to resume uploading a single file.
   *
   * @param {UploadItem} item - Upload item object
   * @return {void}
   */
  resumeFile(item: UploadItem) {
    const { rootFolderId } = this;
    const { overwrite, conflictCallback } = this.options;
    const { api, file, options } = item;

    if (!this.checkConcurrencyLimit()) {
      return;
    }

    const resumeOptions: Object = {
      file,
      folderId: options && options.folderId ? options.folderId : rootFolderId,
      errorCallback: error => this.handleUploadError(item, error),
      progressCallback: event => this.handleUploadProgress(item, event),
      successCallback: entries => this.handleUploadSuccess(item, entries),
      overwrite,
      conflictCallback,
      sessionId: api && api.sessionId ? api.sessionId : null,
      fileId: options && options.fileId ? options.fileId : null,
    };

    item.status = STATUS_IN_PROGRESS;
    delete item.error;
    api.resume(resumeOptions);
    this.options.onUploadProgressUpdate(item, this);
  }

  /**
   * Helper to upload a single file.
   *
   * @param {UploadItem} item - Upload item object
   * @return {void}
   */
  uploadFile(item: UploadItem) {
    const { rootFolderId } = this;
    const {
      overwrite,
      conflictCallback,
      description: fileDescription,
    } = this.options;
    const { api, file, options } = item;

    if (!this.checkConcurrencyLimit()) {
      return;
    }

    const uploadOptions: Object = {
      file,
      folderId: options && options.folderId ? options.folderId : rootFolderId,
      errorCallback: error => this.handleUploadError(item, error),
      progressCallback: event => this.handleUploadProgress(item, event),
      successCallback: entries => this.handleUploadSuccess(item, entries),
      fileId: options && options.fileId ? options.fileId : null,
      overwrite,
      conflictCallback,
      fileDescription,
    };

    item.status = STATUS_IN_PROGRESS;
    api.upload(uploadOptions);
    resin.recordAction({
      ...resinEvent,
      target: 'upload|started',
      data: {
        file_request_url_id: this.fileRequestUrlId,
        form_response_key: this.formResponseKey,
      },
    });
    this.options.onUploadProgressUpdate(item, this);
  }

  /**
   * Updates item based on its status.
   *
   * @private
   * @param {UploadItem} item - The upload item to update
   * @return {void}
   */
  retryErrorItem = (item: UploadItem) => {
    const { chunked, isResumableUploadsEnabled } = this.options;
    const { status, file } = item;
    const isChunkedUpload =
      chunked &&
      file.size > CHUNKED_UPLOAD_MIN_SIZE_BYTES &&
      isMultiputSupported();
    const isResumable =
      isResumableUploadsEnabled && isChunkedUpload && item.api.sessionId;
    switch (status) {
      case STATUS_ERROR:
        if (isResumable) {
          item.bytesUploadedOnLastResume = item.api.totalUploadedBytes;
          this.resumeFile(item);
        } else {
          this.resetFile(item);
          this.uploadFile(item);
        }
        break;
      default:
        break;
    }
  };

  /**
   * Click action button for all uploads in the Uploads Manager with the given status.
   *
   * @private
   * @param {UploadStatus} - the status that items should have for their action button to be clicked
   * @return {void}
   */
  onRetryAllErrorItems = async () => {
    const { itemsInUploadQueue, fetchNewToken } = this;
    const { uploadToken } = await fetchNewToken().catch();
    if (uploadToken) {
      this.updateToken(uploadToken);
    }
    itemsInUploadQueue.forEach(item => {
      if (item.status === STATUS_ERROR) {
        this.retryErrorItem(item);
      }
    });
  };

  /**
   * Click action button for all uploads in the Uploads Manager with the given status.
   *
   * @private
   * @param {UploadStatus} - the status that items should have for their action button to be clicked
   * @return {void}
   */
  onRetryErrorItem = async (item: UploadItem) => {
    const { fetchNewToken } = this;
    if (!fetchNewToken) {
      return;
    }
    const { uploadToken } = await fetchNewToken().catch();
    if (uploadToken) {
      this.updateToken(uploadToken);
    }
    this.retryErrorItem(item);
  };

  updateToken = (uploadToken: string) => {
    const { itemsInUploadQueue } = this;
    set(this, 'options.token', uploadToken);
    itemsInUploadQueue.forEach(item => {
      set(item, 'api.options.token', uploadToken);
      set(item, 'api.xhr.token', uploadToken);
      forEach(get(item, 'api.parts', []), part => {
        set(part, 'xhr.token', uploadToken);
      });
    });
  };

  submit() {
    const { itemQueues } = this;
    const items = union(...map(itemQueues, itemQueue => itemQueue.items));
    this.itemsInUploadQueue = cloneDeep(items);
    this.itemsInUploadQueue.forEach(item => {
      item.api = this.getUploadAPI(item, item.options);
    });
    this.uploadAll();
  }

  hasItemsInUploadQueue() {
    return this.itemsInUploadQueue.length > 0;
  }

  allUploadCompleted() {
    const numItemsCompleted = this.itemsInUploadQueue.filter(
      item => item.status === STATUS_COMPLETE,
    ).length;
    if (numItemsCompleted === this.itemsInUploadQueue.length) {
      return true;
    }
    return false;
  }

  /**
   * Uploads all items in the upload collection.
   *
   * @private
   * @return {void}
   */
  uploadAll() {
    this.itemsInUploadQueue.forEach(uploadItem => {
      if (uploadItem.status === STATUS_PENDING) {
        this.uploadFile(uploadItem);
      }
    });
  }

  /**
   * Handles a successful upload.
   *
   * @private
   * @param {UploadItem} item - Upload item corresponding to success event
   * @param {BoxItem[]} entries - Successfully uploaded Box File objects
   * @return {void}
   */
  async handleUploadSuccess(item: UploadItem, entries?: BoxItem[]) {
    item.progress = 100;
    if (!item.error) {
      item.status = STATUS_COMPLETE;
      if (item.api) item.api.destroy();
    }

    // Cache Box File object of successfully uploaded item
    if (entries && entries.length === 1) {
      const [boxFile] = entries;
      item.boxFile = boxFile;
    }

    if (item.boxFile) {
      resin.recordAction({
        ...resinEvent,
        target: 'upload|success',
        data: {
          file_request_url_id: this.fileRequestUrlId,
          form_response_key: this.formResponseKey,
        },
      });
    }

    this.options.onUploadProgressUpdate(item, this);
    this.uploadAll();
  }

  /**
   * Handles an upload progress event.
   *
   * @private
   * @param {UploadItem} item - Upload item corresponding to progress event
   * @param {ProgressEvent} event - Progress event
   * @return {void}
   */
  handleUploadProgress(item: UploadItem, event: any) {
    if (
      !event.total ||
      item.status === STATUS_COMPLETE ||
      item.status === STATUS_STAGED
    ) {
      return;
    }

    item.progress = Math.min(
      Math.round((event.loaded / event.total) * 100),
      100,
    );
    item.status = item.progress === 100 ? STATUS_STAGED : STATUS_IN_PROGRESS;
    this.options.onUploadProgressUpdate(item, this);
  }

  /**
   * Handles an upload error.
   *
   * @private
   * @param {UploadItem} item - Upload item corresponding to error
   * @param {Error} error - Upload error
   * @return {void}
   */
  handleUploadError(item: UploadItem, error: Error) {
    item.status = STATUS_ERROR;
    item.error = error;

    resin.recordAction({
      ...resinEvent,
      target: 'upload|error',
      data: {
        file_request_url_id: this.fileRequestUrlId,
        form_response_key: this.formResponseKey,
        upload_session_id: get(item, 'api.sessionId'),
      },
    });

    this.options.onUploadProgressUpdate(item, this);
    this.uploadAll();
  }
}

export { Uploader };
export default Uploader;
